import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";
import PageLoader from "./PageLoader";

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState(null);
  const [profileOrders, setProfileOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loadingProfilePhone, setLoadingProfilePhone] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await adminApiRequest({
        script: "get-customers",
      });

      // Ensure no duplicate phones in the list (though backend handles this, safety first)
      const uniqueContacts = [];
      const seenPhones = new Set();

      (res.customers || []).forEach((c) => {
        const phone = String(c.customer_phone || "").trim();
        if (phone && !seenPhones.has(phone)) {
          seenPhones.add(phone);
          uniqueContacts.push(c);
        }
      });

      setCustomers(uniqueContacts);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = async (customer) => {
    if (loadingProfilePhone === customer.customer_phone) return;

    try {
      setLoadingProfilePhone(customer.customer_phone);
      setOrdersLoading(true);
      setExpandedOrderId(null);
      setProfileCustomer(customer);

      const res = await adminApiRequest({
        script: "get-customer-orders",
        data: { customer_phone: customer.customer_phone },
      });

      setProfileOrders(res.orders || []);
      setShowProfileModal(true);
    } catch (err) {
      console.error("Failed to fetch customer orders:", err);
    } finally {
      setOrdersLoading(false);
      setLoadingProfilePhone(null);
    }
  };

  const handleCloseModal = () => {
    setShowProfileModal(false);
    setProfileCustomer(null);
    setProfileOrders([]);
    setExpandedOrderId(null);
  };

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const filteredCustomers = customers.filter((c) => {
    const s = search.toLowerCase();
    return (
      (c.customer_name || "").toLowerCase().includes(s) ||
      (c.customer_email || "").toLowerCase().includes(s) ||
      (c.customer_phone || "").toLowerCase().includes(s)
    );
  });

  const calculateSummary = () => {
    if (!profileOrders.length) {
      return { totalOrders: 0, lastOrderDate: "-", totalSpent: 0 };
    }

    const totalOrders = profileOrders.length;
    const lastOrderDate = new Date(
      profileOrders[0].created_at,
    ).toLocaleDateString("en-GB");
    const totalSpent = profileOrders.reduce(
      (sum, o) => sum + parseFloat(o.total || 0),
      0,
    );

    return { totalOrders, lastOrderDate, totalSpent };
  };

  const { totalOrders, lastOrderDate, totalSpent } = calculateSummary();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="mb-6 flex flex-shrink-0 items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Customers</h1>
          <p className="mt-1 text-sm text-slate-500">
            Total unique contacts: {customers.length}
          </p>
        </div>

        <div className="w-full max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts..."
            className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          />
        </div>
      </div>

      {loading ? (
        <PageLoader message="Fetching Contacts..." />
      ) : (
        <div className="flex-1 overflow-hidden rounded-xl border bg-white shadow-sm">
          <div className="h-full overflow-auto sidebar-scrollbar">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 border-b bg-slate-100">
                <tr className="text-xs uppercase tracking-wide text-slate-600">
                  <th className="px-4 py-3 text-left w-16">#</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer, index) => {
                    const isProfileLoading =
                      loadingProfilePhone === customer.customer_phone;

                    return (
                      <tr
                        key={customer.customer_phone}
                        className="hover:bg-slate-50 transition"
                      >
                        <td className="px-4 py-3 text-slate-500 font-medium">
                          {index + 1}
                        </td>

                        <td className="px-4 py-3 text-slate-700 font-semibold">
                          {customer.customer_name || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {customer.customer_email || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-700 tabular-nums">
                          {customer.customer_phone || "—"}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button title="Call"
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
                              onClick={() =>
                                (window.location.href = `tel:${customer.customer_phone}`)
                              }
                            >
                              <i className="fa fa-phone text-xs"></i>
                            </button>

                            <button title="View"
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:cursor-not-allowed disabled:opacity-70"
                              onClick={() => handleViewProfile(customer)}
                              disabled={isProfileLoading}
                            >
                              {isProfileLoading ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent"></span>
                              ) : (
                                <i className="fa-solid fa-eye text-xs"></i>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-12 text-center text-sm text-slate-400"
                    >
                      No contacts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showProfileModal && profileCustomer && (
        <Modal
          title={profileCustomer.customer_name}
          onClose={handleCloseModal}
          showCancel={false}
          showSubmitButton={false}
        >
          <div className="mb-6 grid grid-cols-3 gap-4 text-sm">
            <div className="rounded-xl bg-slate-100 p-4 text-center ring-1 ring-slate-200">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Orders
              </p>
              <p className="mt-1 text-lg font-bold text-slate-900">
                {totalOrders}
              </p>
            </div>

            <div className="rounded-xl bg-slate-100 p-4 text-center ring-1 ring-slate-200">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Last Order
              </p>
              <p className="mt-1 text-lg font-bold text-slate-900">
                {lastOrderDate}
              </p>
            </div>

            <div className="rounded-xl bg-slate-100 p-4 text-center ring-1 ring-slate-200">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Spent
              </p>
              <p className="mt-1 text-lg font-bold text-emerald-600">
                £{totalSpent.toFixed(2)}
              </p>
            </div>
          </div>

          {ordersLoading ? (
            <PageLoader message="Loading History..." />
          ) : (
            <div className="max-h-[420px] space-y-3 overflow-auto sidebar-scrollbar pr-1">
              {profileOrders.map((order) => {
                const isDelivery =
                  String(order.order_type || "")
                    .trim()
                    .toLowerCase() === "delivery";

                return (
                  <div
                    key={order.id}
                    className="cursor-pointer rounded-xl border border-slate-200 p-4 hover:bg-slate-50 transition"
                    onClick={() => toggleOrder(order.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="font-medium">
                            {new Date(order.created_at).toLocaleDateString(
                              "en-GB",
                            )}
                          </span>

                          {order.order_number && (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-bold">
                              #{order.order_number}
                            </span>
                          )}
                        </div>

                        <p className="text-sm font-bold text-slate-900">
                          Ref: {order.id}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="font-extrabold text-slate-900">
                          £{parseFloat(order.total || 0).toFixed(2)}
                        </p>

                        <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                          {order.order_type}
                        </p>
                      </div>
                    </div>

                    {expandedOrderId === order.id && (
                      <div className="mt-4 space-y-4 text-sm border-t border-slate-100 pt-4">
                        {isDelivery && (
                          <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
                            <p className="font-bold text-blue-800">
                              Delivery Details
                            </p>

                            {order.delivery_address && (
                              <p className="mt-2 text-blue-700">
                                <strong>Address:</strong>{" "}
                                {typeof order.delivery_address === "object"
                                  ? [
                                      order.delivery_address.line1,
                                      order.delivery_address.line2,
                                      order.delivery_address.city,
                                      order.delivery_address.postcode,
                                    ]
                                      .filter(Boolean)
                                      .join(", ")
                                  : order.delivery_address}
                              </p>
                            )}

                            {order.postcode && (
                              <p className="text-blue-700">
                                <strong>Postcode:</strong> {order.postcode}
                              </p>
                            )}

                            <div className="mt-2 flex gap-4 text-xs font-semibold">
                              {order.delivery_fee != null && (
                                <p className="text-blue-600">
                                  Delivery Fee: £
                                  {parseFloat(order.delivery_fee).toFixed(2)}
                                </p>
                              )}

                              {parseFloat(order.service_charge || 0) > 0 && (
                                <p className="text-blue-600">
                                  Service Charge: £
                                  {parseFloat(order.service_charge).toFixed(2)}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="space-y-3">
                          {order.items.map((item) => {
                            const isRemoved =
                              Number(item?.is_removed) === 1 ||
                              item?.is_removed === true;

                            return (
                              <div
                                key={item.id}
                                className={`relative rounded-xl border p-3 transition ${
                                  isRemoved
                                    ? "border-slate-200 bg-slate-50/50 opacity-60"
                                    : "border-slate-200 bg-white"
                                }`}
                              >
                                {isRemoved && (
                                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg">
                                      <i className="fa-solid fa-ban text-sm"></i>
                                    </div>
                                  </div>
                                )}

                                <div className="flex justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <p
                                      className={`text-sm font-bold ${
                                        isRemoved
                                          ? "text-slate-400 line-through"
                                          : "text-slate-900"
                                      }`}
                                    >
                                      {item.name}{" "}
                                      <span className="text-indigo-600 ml-1">
                                        x {item.quantity}
                                      </span>
                                    </p>

                                    {item.required_option_name && (
                                      <p
                                        className={`mt-1 text-xs ${
                                          isRemoved
                                            ? "text-slate-400 line-through"
                                            : "text-slate-600"
                                        }`}
                                      >
                                        <strong>Required:</strong>{" "}
                                        {item.required_option_name}
                                        {parseFloat(
                                          item.required_option_price || 0,
                                        ) > 0
                                          ? ` (£${parseFloat(item.required_option_price).toFixed(2)})`
                                          : ""}
                                      </p>
                                    )}

                                    {(item.selectedOptionalOptions || [])
                                      .length > 0 && (
                                      <div className="mt-2">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                          Optional:
                                        </p>
                                        <ul className="mt-1 space-y-1 text-xs">
                                          {item.selectedOptionalOptions.map(
                                            (opt, idx) => (
                                              <li
                                                key={idx}
                                                className={
                                                  isRemoved
                                                    ? "text-slate-400 line-through"
                                                    : "text-slate-700"
                                                }
                                              >
                                                {opt.name} x {opt.quantity} (£
                                                {parseFloat(
                                                  opt.price || 0,
                                                ).toFixed(2)}
                                                )
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                    {(item.swallowOptions || []).length > 0 && (
                                      <div className="mt-2">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                          Swallow:
                                        </p>
                                        <ul className="mt-1 space-y-1 text-xs">
                                          {item.swallowOptions.map(
                                            (sw, idx) => (
                                              <li
                                                key={idx}
                                                className={
                                                  isRemoved
                                                    ? "text-slate-400 line-through"
                                                    : "text-slate-700"
                                                }
                                              >
                                                {sw.name} x {sw.quantity} (£
                                                {parseFloat(
                                                  sw.price || 0,
                                                ).toFixed(2)}
                                                )
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                    {(item.proteins || []).length > 0 && (
                                      <div className="mt-2">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                          Protein(s):
                                        </p>
                                        <ul className="mt-1 space-y-1 text-xs">
                                          {item.proteins.map((p, idx) => (
                                            <li
                                              key={idx}
                                              className={
                                                isRemoved
                                                  ? "text-slate-400 line-through"
                                                  : "text-slate-700"
                                              }
                                            >
                                              {p.name} x {p.quantity} (£
                                              {parseFloat(p.price || 0).toFixed(
                                                2,
                                              )}
                                              )
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>

                                  <div
                                    className={`text-right font-bold text-sm shrink-0 ${isRemoved ? "text-slate-400 line-through" : "text-slate-900"}`}
                                  >
                                    £
                                    {parseFloat(item.total_price || 0).toFixed(
                                      2,
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {!isDelivery &&
                          parseFloat(order.service_charge || 0) > 0 && (
                            <p className="text-right text-xs font-medium text-slate-500">
                              Service Charge: £
                              {parseFloat(order.service_charge).toFixed(2)}
                            </p>
                          )}

                        {order.notes && (
                          <div className="rounded-xl bg-amber-50 p-3 ring-1 ring-amber-100">
                            <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
                              Notes:
                            </p>
                            <p className="mt-1 text-xs text-amber-700">
                              {order.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
